import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Drawer, Form, Input, Select, message, Popconfirm, Tabs } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { dictionaryService, authService } from '../../services';
import './DictionaryManagement.css';

const { TabPane } = Tabs;

const DictionaryManagement = () => {
    const [currentUser, setCurrentUser] = useState(null);
    const [type, setType] = useState(null); // will be set after user is loaded
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        // fetch current user for UI permission decisions
        let mounted = true;
        authService.getCurrentUser().then(res => {
            if (!mounted) return;
            if (res.data && res.data.code === 200) {
                const user = res.data.data;
                setCurrentUser(user);
                // set default tab based on user role
                if (user.role === 'admin') {
                    setType('weather');
                } else if (user.role === 'manager') {
                    setType('investigator');
                }
            }
        }).catch(() => {
            // ignore
        });
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        if (type) fetchList();
    }, [type, page, limit, currentUser]);

    const fetchList = async () => {
        try {
            setLoading(true);
            // If currentUser has user_unit_id, request unit-specific + global entries
            const userUnitParam = currentUser?.user_unit_id || null;
            const res = await dictionaryService.getDictionaries(type, page, limit, userUnitParam);
            if (res.data.code === 200) {
                setData(res.data.data.data);
                setTotal(res.data.data.total);
            }
        } catch (err) {
            message.error('获取数据字典失败');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditing(null);
        form.resetFields();
        setDrawerVisible(true);
    };

    const handleEdit = (record) => {
        setEditing(record);
        form.setFieldsValue(record);
        setDrawerVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const res = await dictionaryService.deleteDictionary(id);
            if (res.data.code === 200) {
                message.success('删除成功');
                fetchList();
            }
        } catch (err) {
            message.error(err.response?.data?.message || '删除失败');
        }
    };

    const handleSubmit = async (values) => {
        try {
            const payload = { ...values, type };
            if (editing) {
                const res = await dictionaryService.updateDictionary(editing.id, payload);
                if (res.data.code === 200) message.success('更新成功');
            } else {
                const res = await dictionaryService.createDictionary(payload);
                if (res.data.code === 200) message.success('创建成功');
            }
            setDrawerVisible(false);
            fetchList();
        } catch (err) {
            message.error(err.response?.data?.message || '操作失败');
        }
    };

    const columns = [
        { title: '编码', dataIndex: 'code', key: 'code', width: 160 },
        { title: '名称', dataIndex: 'name', key: 'name', width: '50%' },
        { title: '排序', dataIndex: 'sort', key: 'sort', width: 80 },
        {
            title: '操作', key: 'action', width: 180, render: (_, record) => {
                // determine if current user can edit/delete this record
                const isInvestigatorOrDrawer = ['investigator', 'drawer'].includes(type);
                const role = currentUser?.role;
                const ownUnit = currentUser?.user_unit_id || null;
                const recUnit = record.user_unit_id || null;

                let canManage = false;
                if (isInvestigatorOrDrawer) {
                    // only manager of same unit may manage investigator/drawer
                    canManage = (role === 'manager' && recUnit === ownUnit);
                } else {
                    // for global dictionary types (weather, road_surface), only admin can manage
                    canManage = (role === 'admin');
                }

                if (!canManage) return null;

                return (
                    <Space>
                        <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
                        <Popconfirm title="确认删除该条目吗？" onConfirm={() => handleDelete(record.id)}>
                            <Button danger size="small" icon={<DeleteOutlined />}>删除</Button>
                        </Popconfirm>
                    </Space>
                );
            }
        }
    ];

    return (
        <div className="dictionary-management">
            <Card title="数据字典管理" className="management-card">
                <Tabs activeKey={type} onChange={(k) => { setType(k); setPage(1); }}>
                    {currentUser?.role === 'admin' && (
                        <>
                            <TabPane tab="天气字典" key="weather" />
                            <TabPane tab="路面性质字典" key="road_surface" />
                        </>
                    )}
                    {currentUser?.role === 'manager' && (
                        <>
                            <TabPane tab="勘查员字典" key="investigator" />
                            <TabPane tab="绘图员字典" key="drawer" />
                        </>
                    )}
                </Tabs>

                <Space style={{ marginBottom: 16 }}>
                    {
                        // determine if create button should be shown for current user/type
                        (() => {
                            const role = currentUser?.role;
                            const isInvestigatorOrDrawer = ['investigator', 'drawer'].includes(type);
                            if (isInvestigatorOrDrawer) {
                                // only manager can create these (backend enforces unit binding)
                                return role === 'manager' ? (
                                    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新增条目</Button>
                                ) : null;
                            }
                            // for global dictionary types (weather, road_surface), only admin can create
                            return (role === 'admin') ? (
                                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新增条目</Button>
                            ) : null;
                        })()
                    }
                </Space>

                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    loading={loading}
                    pagination={{ current: page, pageSize: limit, total, onChange: (p) => setPage(p), onShowSizeChange: (_, s) => setLimit(s) }}
                />
            </Card>

            <Drawer title={editing ? '编辑条目' : '新增条目'} open={drawerVisible} onClose={() => setDrawerVisible(false)} width={420}>
                <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ sort: 0 }}>
                    <Form.Item label="编码 (可选)" name="code">
                        <Input />
                    </Form.Item>
                    <Form.Item label="名称" name="name" rules={[{ required: true, message: '请输入名称' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="排序" name="sort">
                        <Input type="number" />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" style={{ width: '100%' }}>{editing ? '更新' : '创建'}</Button>
                    </Form.Item>
                </Form>
            </Drawer>
        </div>
    );
};

export default DictionaryManagement;
